import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();
const API_BASE_URL = "https://trainer-portal-78f5.onrender.com/api";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  // On mount, verify token
  useEffect(() => {
    const savedToken = localStorage.getItem("authToken");
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfileWithToken = async (authToken) => {
    const profileRes = await fetch(`${API_BASE_URL}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!profileRes.ok) {
      return null;
    }

    const profileData = await profileRes.json();
    return profileData?.trainer || null;
  };

  // 🔐 Verify saved token
  const verifyToken = async (savedToken) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${savedToken}`,
          "Content-Type": "application/json",
        },
      });

      if (res.ok) {
        const data = await res.json();
        setToken(savedToken);
        setUser(data.trainer);
        setOnboardingCompleted(data.trainer?.onboardingCompleted || false);

        // Fetch full profile
        const profileRes = await fetch(`${API_BASE_URL}/auth/profile`, {
          headers: {
            Authorization: `Bearer ${savedToken}`,
            "Content-Type": "application/json",
          },
        });

        if (profileRes.ok) {
          const profileData = await profileRes.json();
          setUser(profileData.trainer);
          setOnboardingCompleted(
            profileData.trainer?.onboardingCompleted || false,
          );
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error("Token verification failed", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  //  Normal login
  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }), //  send role too
      });

      const data = await res.json();

      if (res.ok) {
        const profileTrainer = await fetchProfileWithToken(data.token);
        const trainerData = profileTrainer || data.trainer;

        setToken(data.token);
        setUser({
          ...trainerData,
          role: trainerData?.role, // keep role in context
        });
        setOnboardingCompleted(Boolean(trainerData?.onboardingCompleted));
        localStorage.setItem("authToken", data.token);
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || "Login failed" };
      }
    } catch (err) {
      return { success: false, message: "Network error" };
    }
  };

  //  Request OTP
  const requestOTPLogin = async (email, password) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/request-otp-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || "Failed to send OTP" };
      }
    } catch (err) {
      return { success: false, message: "Network error" };
    }
  };

  //   Verify OTP
  const verifyOTP = async (email, otp) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });

      const data = await res.json();

      if (res.ok) {
        const profileTrainer = await fetchProfileWithToken(data.token);
        const trainerData = profileTrainer || data.trainer;

        setToken(data.token);
        setUser(trainerData);
        setOnboardingCompleted(Boolean(trainerData?.onboardingCompleted));
        localStorage.setItem("authToken", data.token);
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || "Invalid OTP" };
      }
    } catch (err) {
      return { success: false, message: "OTP verification failed" };
    }
  };

  //  Logout
  // Called after trainer passes the onboarding quiz
  const setOnboardingDone = () => {
    setOnboardingCompleted(true);
    setUser((prev) => (prev ? { ...prev, onboardingCompleted: true } : prev));
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setUser(null);
    setToken(null);
    setOnboardingCompleted(false);
  };

  //  Update profile (optional)
  const updateProfile = async (profileData) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      const data = await res.json();
      if (res.ok) {
        setUser(data.trainer);
        return { success: true, message: "Profile updated" };
      } else {
        return { success: false, message: data.error || "Update failed" };
      }
    } catch (err) {
      return { success: false, message: "Profile update failed" };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        onboardingCompleted,
        login,
        logout,
        setOnboardingDone,
        requestOTPLogin,
        verifyOTP,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
